import {Inject, OnDestroy, Injectable, InjectionToken} from '@angular/core';
import {Subject, Observable} from "rxjs";
import {IWindowEventsRegister, WINDOW_TOKEN} from "./window.events.browser.service";

export let WINDOW_LISTENER_TOKEN = new InjectionToken("generic.event.window");

/*
* The service manages window events, instead of using the decorator @HostListener('window: …').
*
* @HostListener('window: …') VS WindowHostListenerService:
* 	1. HostListener runs change detection when the function end.
* 	2. With the service you can listening to the window events only you need.
* 	3. If component is part of session, then when the session not on the DOM the service stops emit events.
*
* To use:
* 	Call the function getObservable.
*
* Add new window event:
* 	Add the event name to WindowEvent.
*/
export enum WindowEvent {
	click, mouseup, mousemove, resize, keydown, keyup, touchend, scroll, mousedown, touchstart, keypress, blur,contextmenu,drag
}

@Injectable()
export class WindowHostListenerService implements OnDestroy {

	private events: { [eventName: string]: { subject: Subject<UIEvent>, function: (event) => void, useCapture: boolean }[] } = {};
	private eventListenerOn: boolean;
	private duringOffline:boolean;
	private offSubject = new Subject();

	constructor(@Inject(WINDOW_TOKEN) private window: IWindowEventsRegister) {
	}

	public init(): void {
		this.on();
	}

	public ngOnDestroy(): void {
		this.destroy();
	}

	public destroy() {
		Object.keys(this.events).forEach((event) => {
			this.events[event].forEach((item) => {
				if (item.subject) {
					item.subject.complete();
					item.subject = null;
				}
				this.window.removeEventListener(<any> event, item.function, item.useCapture);
			});
		});

		if (this.offSubject) {
			this.offSubject.complete();
		}
	}

	// When useCapture = true, window events will be dispatched to the registered listener
	// before being dispatched to any EventTarget beneath it in the DOM tree.
	public getObservable<T extends UIEvent>(event: WindowEvent, useCapture: boolean = true): Observable<T> {
		if (!this.events[WindowEvent[event]] ||
			(this.events[WindowEvent[event]].length === 1 && this.events[WindowEvent[event]][0].useCapture !== useCapture)) {
			this.addEvent(event, useCapture);
		}

		return <any>this.events[WindowEvent[event]]
			.find((item) => item.useCapture === useCapture)
			.subject.asObservable();
	}

	public getOffObservable() {
		return this.offSubject.asObservable();
	}

	public on() {
		this.duringOffline=false;
		this.eventListenerOn = true;
	}

	public off() {
		this.duringOffline=true;

		// we want the event listener to keep sending events because we might want to be notified
		// of events while the session is going offline, but we need to inform the session - for example
		// if it's a resize event - the session should not be affected if it happens while taking it off the DOM
		setTimeout(() => {
			// the offSubject is a miss-design - the hot keys depend on it instead of integrating with the sessions mechanism directly
			this.offSubject.next();
			this.eventListenerOn = false;
		});
	}

	private addEvent(event: WindowEvent, useCapture: boolean) {
		let subject = new Subject<UIEvent>();
		let fun = (e:UIEvent) => {
			if (this.eventListenerOn) {
				e["duringOffline"]=this.duringOffline;
				subject.next(e);
			}
		};

		this.events[WindowEvent[event]] = this.events[WindowEvent[event]] ? this.events[WindowEvent[event]] : [];
		this.events[WindowEvent[event]].push({subject: subject, function: fun, useCapture: useCapture});

		this.window.addEventListener(<any> WindowEvent[event], fun, useCapture);
	}
}
